import {
  AccountBurnRate,
  UserNotification,
  UserNotificationResponse,
  UserPayout,
  UserPayoutResponse,
  UserTransaction,
  UserTransactionResponse
} from "@/types";
import { jsonify } from "@/utils";
import { executeAPIRequest } from "@/utils/api";
import { getNotification } from "@/utils/mapping";
import { stringify } from "qs";
import { create } from "zustand";
import { createJSONStorage, persist } from "zustand/middleware";
// import { PublicKey } from "@solana/web3.js";
import { INTELLIGENCE_DEV_PRICE_MODEL, INTELLIGENCE_PRICE_MODEL } from "@/constants/user";
import env from "@/env";
import { isExplorerOnlyMode } from "@/utils/explorerOnlyMode";
import {
  BalanceType,
  ManageSubscriptionResponse,
  NotificationType,
  SubscriptionResponse,
  SubscriptionStatusResponse,
  UserInfo,
  UserWalletType,
  DiscountedPlan
} from "@/types/user";
import { getGoogleAnalyticsHeaders } from "@/utils/analytics";
import { toFriendlyError } from "@/utils/api";
import localCookies from "@/utils/cookie";
import { toNumber } from "@/utils/number";
import { errorIndicatesRequiresLogin, toStringWithLoginVersionSuffix } from "@/utils/user";
import { v4 as uuidv4 } from "uuid";

const UUID_PATTERN = /^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;

type UseUserStoreProps = {
  userId: string;
  /** Active theme applied to the document for the current product context. */
  theme: string;
  /** Persisted theme preference for the main app (cloud / ai / combined shell). */
  appTheme: string;
  /** Persisted theme preference for explorer-only mode. */
  explorerTheme: string;
  supplier?: string;
  hasSupplier?: boolean;
  balance?: number;
  balances?: { [k in BalanceType]: number };
  /** Server-resolved purchased-credits exemption (CLO-658); undefined until balances load. */
  purchasedCreditsExempt?: boolean;
  isLoadingBalances?: boolean;
  /** Total $/hour of the user's running PAYG clusters; undefined until fetched. */
  burnRate?: AccountBurnRate;
  isRequiresLoginModalOpen: boolean;
  setRequiresLoginModalOpen: (isOpen: boolean) => void;
  requiresLoginMode?: string;
  setRequiresLoginMode: (mode: string) => void;
  fetchNotificationsRequest: (options: {
    type?: "DEVICE" | "ACCOUNT";
    priority?: NotificationType;
    page: number;
    limit: number;
    seen?: boolean;
  }) => Promise<{
    resultCount: number;
    results: UserNotification[];
  }>;
  markNotificationAsRead: (
    notificationId: string,
    show?: boolean
  ) => Promise<{ status: string; data: string }>;
  markAllNotificatiosnAsRead: () => void;
  fetchBalance: () => Promise<number>;
  fetchBalances: (refresh?: boolean) => Promise<{ [k in BalanceType]: number }>;
  fetchBurnRate: (refresh?: boolean) => Promise<AccountBurnRate | undefined>;
  initialisePayout: ({
    wallet,
    amount,
    recaptchaToken
  }: {
    wallet: string;
    amount: number;
    recaptchaToken: string;
  }) => Promise<UserPayout>;
  fetchWalletAddresses: () => Promise<{
    spl_address?: string;
    aptos_address?: string;
    solana_addresses?: UserWalletType[];
    aptos_addresses?: UserWalletType[];
  }>;
  fetchSolanaAddress: () => Promise<string | null>;
  updateSolanaAddress: (address: string) => Promise<void>;
  updateAptosAddress: (address: string) => Promise<void>;
  generatePaymentLink: (options: {
    name: string;
    amount: number;
    type?: string;
    currency?: "usdc" | "iocoin";
    resourceId?: string;
  }) => Promise<{ paymentLink: string; resourceId: string }>;
  generateStripePaymentLink: (options: {
    amount: number;
    resourceId?: string;
  }) => Promise<{ paymentLink: string; resourceId: string }>;
  topUpUserCredits: (options: {
    amount: number;
    resourceId: string;
    paymentMethod?: "crypto" | "fiat";
    redirectUrl?: string;
  }) => Promise<{ paymentLink: string; resourceId: string }>;
  assignSupplier: (supplierId: string) => Promise<void>;
  updateTheme: (mode: string) => void;
  applyThemeForCurrentMode: () => void;
  fetchTransactionsRequest: (options: {
    page: number;
    limit: number;
    sorted?: string;
    type?: string;
    platform?: string;
    deviceName?: string;
    clusterId?: string;
    hideCharges?: boolean;
    date?: { startDate?: string; endDate?: string };
    paymentSource?: string;
  }) => Promise<{
    resultCount: number;
    results: UserTransaction[];
  }>;
  fetchTransactionDetail: (id: string) => Promise<UserTransaction>;
  fetchTransactions: () => Promise<UserTransaction[]>;
  setUserId: (userId?: string) => void;
  fetchPoints: () => Promise<{ total: number; gpu: number; cpu: number }>;
  userInfo?: UserInfo;
  fetchUserInfo: () => Promise<UserInfo>;
  saveWalletAddress: (formData: {
    solana_address?: string;
    aptos_address?: string;
  }) => Promise<boolean>;
  markPrimaryWallet: (walletAddress: string) => Promise<boolean>;
  deleteWalletAddress: (walletAddress: string) => Promise<boolean>;
  subscribeIntelligencePlan: (
    priceModel: string | "INTELLIGENCE_MONTHLY" | "INTELLIGENCE_DEV_MONTHLY",
    redirectUrl?: string
  ) => Promise<SubscriptionResponse>;
  getManageSubscriptionUrl: () => Promise<ManageSubscriptionResponse>;
  fetchSubscriptions: () => Promise<SubscriptionStatusResponse | null>;
  currentSubscription: SubscriptionStatusResponse["data"] | null;
  submitSupportRequest: (message: string) => Promise<void>;
  discountInfo?: { code: string; valid: boolean; plans: DiscountedPlan[] } | null;
  validateDiscountCode: (code: string) => Promise<boolean>;
  sendDripEvent: (event: string) => void;
};

const getDefaultAppTheme = () => (env.LIGHT_MODE_ENABLED ? "light" : "dark");

const getDefaultExplorerTheme = () => "dark";

const resolveTheme = (mode: string) => {
  return mode === "light" && !env.LIGHT_MODE_ENABLED ? "dark" : mode;
};

const normalizeStoredTheme = (mode: string | undefined, fallback: string) => {
  if (mode === "dark" || mode === "light") {
    return mode;
  }
  return fallback;
};

const getThemeKeyForCurrentMode = () =>
  isExplorerOnlyMode() ? ("explorerTheme" as const) : ("appTheme" as const);

const applyThemeToDocument = (mode: string) => {
  const html = document.getElementsByTagName("html")[0];
  if (html) html.setAttribute("data-theme", mode);

  const isDark = mode === "dark";
  document.documentElement.classList.toggle("dark", isDark);
  document.body.classList.toggle("dark", isDark);
};

const emptyBalances = (): { [k in BalanceType]: number } => ({
  [BalanceType.CLOUD]: 0,
  [BalanceType.IOCLOUD]: 0,
  [BalanceType.WORKER]: 0,
  [BalanceType.BC8]: 0,
  [BalanceType.IOWORKER]: 0,
  [BalanceType.CREDITS]: 0,
  [BalanceType.CREDITS_WITHDRAWABLE]: 0,
  [BalanceType.CREDITS_COURTESY]: 0,
  [BalanceType.ALL_CREDITS]: 0,
  [BalanceType.CREDITS_USED]: 0
});

/** In-flight balances request — keyed by userId so auth io_id→UUID swaps don't stick at $0. */
let balancesFetch: Promise<{ [k in BalanceType]: number }> | null = null;
let balancesFetchUserId: string | null = null;

/** Same single-flight treatment for the burn rate, for the same identity-swap reason. */
let burnRateFetch: Promise<AccountBurnRate | undefined> | null = null;
let burnRateFetchUserId: string | null = null;

export const useUserStore = create(
  persist<UseUserStoreProps>(
    (set, get) => ({
      isRequiresLoginModalOpen: false,
      setRequiresLoginModalOpen: (isOpen: boolean) => {
        set({ isRequiresLoginModalOpen: isOpen });
      },
      setRequiresLoginMode: (mode) => {
        set({ requiresLoginMode: mode });
      },
      userId: "",
      appTheme: getDefaultAppTheme(),
      explorerTheme: getDefaultExplorerTheme(),
      theme: isExplorerOnlyMode() ? getDefaultExplorerTheme() : getDefaultAppTheme(),
      setUserId: (userId) => {
        const previousUserId = get().userId;
        if (previousUserId === userId) {
          return;
        }
        // Auth enrichment can swap WorkOS io_id ↔ internal UUID. Drop cached balances and
        // unlock so the next fetchBalances for the new identity is not blocked.
        balancesFetch = null;
        balancesFetchUserId = null;
        burnRateFetch = null;
        burnRateFetchUserId = null;
        set({
          userId,
          balances: undefined,
          isLoadingBalances: false,
          burnRate: undefined
        });
      },
      hasSupplier: false,
      currentSubscription: null,
      applyThemeForCurrentMode: () => {
        const { appTheme, explorerTheme } = get();
        const storedTheme = isExplorerOnlyMode() ? explorerTheme : appTheme;
        const effectiveMode = resolveTheme(storedTheme);
        set({ theme: effectiveMode });
        applyThemeToDocument(effectiveMode);
      },
      updateTheme: (mode) => {
        const effectiveMode = resolveTheme(mode);
        const themeKey = getThemeKeyForCurrentMode();
        set({
          theme: effectiveMode,
          [themeKey]: effectiveMode
        });
        applyThemeToDocument(effectiveMode);
      },
      initialisePayout: async ({ wallet, amount, recaptchaToken }) => {
        try {
          const response = await executeAPIRequest<{
            data: UserPayoutResponse;
            status: string;
          }>({
            method: "post",
            url: `/payout?${stringify({ solana_address: wallet, usdc_amount: amount })}`,
            options: {
              headers: {
                recaptchaToken
              }
            }
          });

          return normaliseUserPayout(response.data);
        } catch (e) {
          throw e;
        }
      },
      fetchBalance: async () => {
        const { userId } = get();

        const response = await executeAPIRequest<{
          data: number;
          status: string;
        }>({
          method: "get",
          url: `/io-cloud/users/${userId}/balance`,
          options: {
            errorPrefix: "unable to retrieve balance"
          }
        });

        set({ balance: response.data });

        return response.data;
      },
      fetchBalances: async (refresh) => {
        const { balances, userId } = get();

        if (!userId) {
          // Do not write placeholder balances — that poisons the cache and makes
          // `(!refresh && balances)` skip the real fetch after userId is set.
          set({ isLoadingBalances: false });
          return emptyBalances();
        }

        if (!refresh && balances) {
          return balances;
        }

        // Reuse in-flight request for the same user; start a new one if identity changed.
        if (balancesFetch && balancesFetchUserId === userId) {
          return balancesFetch;
        }

        const requestedUserId = userId;
        balancesFetchUserId = requestedUserId;
        balancesFetch = (async () => {
          set({ isLoadingBalances: true });

          try {
            const cloudResponse = await executeAPIRequest<{
              cloud_balance: number;
              cloud_iocoin_balance: number;
              worker_balance: number;
              worker_io_coin_balance: number;
              credits_balance: number;
              credits_withdrawable_balance: number;
              credits_courtesy_balance?: number;
              purchased_credits_exempt?: boolean;
              status: string;
              all_credits: number;
              credits_used: number;
            }>({
              method: "get",
              url: `/io-cloud/users/${requestedUserId}/balances`,
              options: {
                errorPrefix: "unable to retrieve balance"
              }
            });

            // Auth enrichment can swap WorkOS io_id ↔ internal UUID while this request
            // is in flight — drop stale responses so we don't cache zeros under the wrong id.
            if (get().userId !== requestedUserId) {
              return emptyBalances();
            }

            const newBalances = emptyBalances();
            newBalances[BalanceType.CLOUD] = cloudResponse.cloud_balance;
            newBalances[BalanceType.IOCLOUD] = cloudResponse.cloud_iocoin_balance;
            newBalances[BalanceType.WORKER] = cloudResponse.worker_balance;
            newBalances[BalanceType.IOWORKER] = cloudResponse.worker_io_coin_balance;
            newBalances[BalanceType.CREDITS] = cloudResponse.credits_balance;
            newBalances[BalanceType.CREDITS_WITHDRAWABLE] =
              cloudResponse.credits_withdrawable_balance;
            newBalances[BalanceType.CREDITS_COURTESY] =
              Number(cloudResponse.credits_courtesy_balance) || 0;
            newBalances[BalanceType.ALL_CREDITS] = cloudResponse.all_credits;
            newBalances[BalanceType.CREDITS_USED] = cloudResponse.credits_used;

            // Local testing override: fake empty credits so card UI/payment paths stay available.
            if (env.FORCE_ZERO_CREDITS) {
              newBalances[BalanceType.CREDITS] = 0;
              newBalances[BalanceType.CREDITS_WITHDRAWABLE] = 0;
              newBalances[BalanceType.CREDITS_COURTESY] = 0;
              newBalances[BalanceType.ALL_CREDITS] = 0;
            }

            set({
              balances: jsonify(newBalances),
              // Server-resolved CLO-658 exemption (override precedence already applied);
              // falls back to the @io.net email rule when absent (see isPurchasedCreditsExempt).
              purchasedCreditsExempt:
                typeof cloudResponse.purchased_credits_exempt === "boolean"
                  ? cloudResponse.purchased_credits_exempt
                  : undefined,
              isLoadingBalances: false
            });
            return newBalances;
          } catch (e) {
            if (get().userId !== requestedUserId) {
              return emptyBalances();
            }

            set({ isLoadingBalances: false });

            // Do not cache placeholder zeros on auth/network failures. CORS can
            // block `ai.*` while allowing `cloud.*` (Axios → "Network problem");
            // writing zeros here sticks the sidebar at $0 forever for that session.
            if (errorIndicatesRequiresLogin(e)) {
              return get().balances || emptyBalances();
            }

            throw e;
          } finally {
            if (balancesFetchUserId === requestedUserId) {
              balancesFetch = null;
              balancesFetchUserId = null;
            }
          }
        })();

        return balancesFetch;
      },
      fetchBurnRate: async (refresh) => {
        const { burnRate, userId } = get();

        if (!userId) {
          return undefined;
        }

        if (!refresh && burnRate) {
          return burnRate;
        }

        if (burnRateFetch && burnRateFetchUserId === userId) {
          return burnRateFetch;
        }

        const requestedUserId = userId;
        burnRateFetchUserId = requestedUserId;
        burnRateFetch = (async () => {
          try {
            const response = await executeAPIRequest<{
              data: {
                hourly_rate: number | null;
                cluster_count: number;
              };
            }>({
              method: "get",
              url: `/io-cloud/users/${requestedUserId}/burn-rate`,
              options: { errorPrefix: "unable to retrieve burn rate" }
            });

            if (get().userId !== requestedUserId) {
              return undefined;
            }

            const newBurnRate: AccountBurnRate = {
              hourlyRate: response.data.hourly_rate,
              clusterCount: response.data.cluster_count
            };
            set({ burnRate: newBurnRate });
            return newBurnRate;
          } catch (e) {
            // Never fatal: the burn rate is decoration next to the balance, so a failure
            // leaves the previous value (or nothing) rather than breaking the page.
            if (errorIndicatesRequiresLogin(e)) {
              return get().burnRate;
            }
            throw e;
          } finally {
            if (burnRateFetchUserId === requestedUserId) {
              burnRateFetch = null;
              burnRateFetchUserId = null;
            }
          }
        })();

        return burnRateFetch;
      },
      fetchNotificationsRequest: async ({ type, page, limit, seen, priority }) => {
        const response = await executeAPIRequest<{
          status: string;
          total: number;
          data: UserNotificationResponse[];
        }>({
          method: "get",
          url: `/notifications/?${stringify({
            priority,
            obj_type: type,
            seen,
            page,
            page_size: limit
          })}`
        });

        const { data, total } = response;

        return {
          resultCount: total,
          results: data.map((result) => normaliseUserNotification(result))
        };
      },
      markNotificationAsRead: async (id, show) => {
        return await executeAPIRequest<{
          status: string;
          data: string;
        }>({
          method: "patch",
          url: `/notifications/${id}/seen?show_again=${show ? 1 : 0}`
        });
      },
      markAllNotificatiosnAsRead: async () => {
        await executeAPIRequest<{
          status: string;
          data: string;
        }>({
          method: "patch",
          url: `/notifications/seen`
        });
      },
      fetchWalletAddresses: async () => {
        try {
          const response = await executeAPIRequest<{
            status: string;
            data: UserWalletType[];
          }>({
            method: "get",
            url: "/io-user/wallets"
          });

          const responseData = response.data;

          return {
            solana_addresses: walletTypeAddress("solana", responseData),
            aptos_addresses: walletTypeAddress("aptos", responseData)
          };
        } catch (e) {
          return {
            solana_addresses: [],
            aptos_addresses: []
          };
        }
      },
      fetchSolanaAddress: async () => {
        const { userId } = get();

        try {
          const response = await executeAPIRequest<{
            data: string | null;
            status: string;
          }>({
            method: "get",
            url: `/io-worker/users/${userId}/solana_address`
          });

          return response.data;
        } catch (e) {
          if (e instanceof Error) {
            if (e.message === "Solana address not found") {
              return null;
            }

            throw e;
          }
        }

        return null;
      },
      updateSolanaAddress: async (address: string) => {
        const { userId } = get();

        try {
          await executeAPIRequest<{
            data: boolean;
            status: string;
          }>({
            method: "put",
            url: `/io-worker/users/${userId}/update_solana_address?${stringify({
              ...(address.length > 0
                ? {
                    solana_address: address
                  }
                : {})
            })}`
          });
        } catch (e) {
          throw toFriendlyError(e);
        }
      },
      updateAptosAddress: async (address: string) => {
        const { userId } = get();

        try {
          await executeAPIRequest<{
            data: boolean;
            status: string;
          }>({
            method: "put",
            url: `/io-worker/users/${userId}/update_aptos_address?${stringify({
              ...(address.length > 0
                ? {
                    aptos_address: address
                  }
                : {})
            })}`
          });
        } catch (e) {
          throw toFriendlyError(e);
        }
      },
      generatePaymentLink: async ({ name, amount, type, currency = "usdc", resourceId }) => {
        try {
          const { userId } = get();
          const data: Record<string, string> = {};

          if (resourceId) {
            data.resource_id = resourceId;
          }
          if (currency === "usdc") {
            data.payment_source = "credits";
          }
          const response = await executeAPIRequest<{
            data: string;
            resource_id: string;
            status: string;
          }>({
            method: "post",
            url: `/io-cloud/clusters/generate-payment-link?${stringify({
              cluster_name: name,
              currency,
              ...(type
                ? {
                    cluster_type: type === "kubernetes" ? "k8s_cluster" : "cluster"
                  }
                : {}),
              amount: Math.abs(amount),
              user_id: userId
            })}`,
            options: {
              headers: {
                ...getGoogleAnalyticsHeaders()
              },
              data,
              errorPrefix: "unable to generate payment link"
            }
          });

          return {
            resourceId: response.resource_id,
            paymentLink: response.data
          };
        } catch (e) {
          throw e;
        }
      },
      generateStripePaymentLink: async ({
        amount,
        resourceId
      }: {
        amount: number;
        resourceId?: string;
      }) => {
        try {
          const response = await executeAPIRequest<{
            data: string;
            status: string;
            resource_id: string;
          }>({
            method: "post",
            url: "/io-cloud/clusters/stripe/generate-payment-link",
            options: {
              errorPrefix: "unable to generate payment link",
              headers: {
                ...getGoogleAnalyticsHeaders()
              },
              data: {
                amount,
                ...(resourceId
                  ? {
                      resource_id: resourceId
                    }
                  : {})
              }
            }
          });

          return {
            resourceId: resourceId || uuidv4(),
            paymentLink: response.data
          };
        } catch (e) {
          throw e;
        }
      },
      topUpUserCredits: async ({
        amount,
        resourceId,
        paymentMethod = "crypto",
        redirectUrl
      }: {
        amount: number;
        resourceId: string;
        paymentMethod?: "crypto" | "fiat";
        redirectUrl?: string;
      }) => {
        try {
          const response = await executeAPIRequest<{
            status: string;
            data: {
              payment_link: string;
            };
          }>({
            method: "post",
            url: "/io-cloud/users/top-up-credits",
            options: {
              errorPrefix: "unable to generate payment link",
              headers: {
                ...getGoogleAnalyticsHeaders()
              },
              data: {
                amount,
                payment_method: paymentMethod,
                ...(redirectUrl
                  ? {
                      redirect_url: redirectUrl
                    }
                  : {}),
                ...(resourceId
                  ? {
                      resource_id: resourceId
                    }
                  : {})
              }
            }
          });

          return {
            resourceId,
            paymentLink: response.data.payment_link
          };
        } catch (e) {
          throw e;
        }
      },
      assignSupplier: async (supplierId: string) => {
        const { userId } = get();

        await executeAPIRequest<{
          data: string;
          status: string;
        }>({
          method: "put",
          url: `/io-cloud/users/${userId}/assign-user-supplier?${stringify({
            supplier_id: supplierId
          })}`
        });
      },
      fetchTransactionsRequest: async ({
        page,
        limit,
        sorted,
        type,
        platform,
        date,
        deviceName,
        clusterId,
        hideCharges,
        paymentSource
      }) => {
        const trimmedClusterId = clusterId?.trim();
        // An incomplete or invalid ID cannot match a cluster. Keep the search active
        // without sending a value the UUID API parameter would reject.
        if (trimmedClusterId && !UUID_PATTERN.test(trimmedClusterId)) {
          return { resultCount: 0, results: [] };
        }

        try {
          const response = await executeAPIRequest<{
            status: string;
            page_no: number;
            page_size: number;
            data: UserTransactionResponse[];
            total_results: number;
          }>({
            method: "get",
            url: `/transactions?${stringify({
              ...{
                page,
                page_size: limit,
                sorted
              },
              ...(date?.startDate && date?.endDate
                ? { start_date: date.startDate + "T00:00:00", end_date: date.endDate + "T23:59:59" }
                : {}),
              ...(type ? { type } : {}),
              ...(deviceName ? { device_name: deviceName } : {}),
              ...(trimmedClusterId ? { cluster_id: trimmedClusterId } : {}),
              ...(hideCharges !== undefined ? { hide_charges: hideCharges } : {}),
              ...(platform ? { platform } : {}),
              ...(paymentSource ? { payment_source: paymentSource } : {})
            })}`
          });
          return {
            resultCount: response.total_results,
            results: response.data.map((transaction) => {
              return normaliseUserTransaction(transaction);
            })
          };
        } catch (e) {
          if (errorIndicatesRequiresLogin(e)) {
            return {
              resultCount: 0,
              results: []
            };
          }

          throw toFriendlyError(e);
        }
      },
      fetchTransactionDetail: async (id) => {
        try {
          const response = await executeAPIRequest<{
            status: string;
            data: UserTransactionResponse;
          }>({
            method: "get",
            url: `/transactions/${id}`
          });
          return normaliseUserTransaction(response.data);
        } catch (e) {
          throw toFriendlyError(e);
        }
      },
      fetchTransactions: async () => {
        const { userId } = get();

        const response = await executeAPIRequest<{
          data: UserTransactionResponse[];
          status: string;
        }>({
          method: "get",
          url: `/io-worker/users/${userId}/transaction`
        });

        return response.data.map((transaction) => {
          return normaliseUserTransaction(transaction);
        });
      },
      fetchPoints: async () => {
        const { userId } = get();

        const response = await executeAPIRequest<{
          data: number;
          status: string;
          gpu_points: number;
          cpu_points: number;
          gpu_points_season2: number;
          cpu_points_season2: number;
          gpu_points_season3: number;
          cpu_points_season3: number;
        }>({
          method: "get",
          url: `/io-cloud/users/${userId}/points`
        });

        return {
          total:
            response.data +
            (env.FEATUREFLAG_POINTS_SEASONS_ENABLED
              ? toNumber(response.gpu_points_season2) +
                toNumber(response.cpu_points_season2) +
                toNumber(response.gpu_points_season3) +
                toNumber(response.cpu_points_season3)
              : 0),
          gpu: response.gpu_points,
          cpu: response.cpu_points,
          gpu_points_season1: response.gpu_points,
          cpu_points_season1: response.cpu_points,
          ...response
        };
      },
      fetchUserInfo: async () => {
        try {
          const response = await executeAPIRequest<{
            data: {
              block_rewards_suspicious?: boolean;
              is_worker?: boolean;
              is_cloud?: boolean;
            };
          }>({
            method: "get",
            url: `/me`
          });

          const { data } = response;
          const userInfo = {
            blockRewardsSuspicious: data.block_rewards_suspicious === true,
            isWorker: data.is_worker === true,
            isCloud: data.is_cloud === true
          } as UserInfo;

          set({ userInfo });

          return userInfo;
        } catch (e) {
          throw e;
        }
      },
      saveWalletAddress: async (formData) => {
        try {
          const response = await executeAPIRequest<{
            status: string;
            data: boolean;
          }>({
            method: "put",
            url: "/io-user/wallets",
            options: {
              data: formData
            }
          });

          return response.data;
        } catch (e) {
          throw e;
        }
      },
      markPrimaryWallet: async (walletAddress) => {
        try {
          const response = await executeAPIRequest<{
            status: string;
            data: boolean;
          }>({
            method: "post",
            url: `/io-user/wallets/${walletAddress}/primary`
          });

          return response.data;
        } catch (e) {
          return false;
        }
      },
      deleteWalletAddress: async (walletAddress) => {
        try {
          const response = await executeAPIRequest<{
            status: string;
            data: boolean;
          }>({
            method: "delete",
            url: `/io-user/wallets/${walletAddress}`
          });

          return response.data;
        } catch (e) {
          return false;
        }
      },
      subscribeIntelligencePlan: async (
        priceModel: string | "INTELLIGENCE_MONTHLY" | "INTELLIGENCE_DEV_MONTHLY",
        redirectUrl?: string
      ) => {
        const discountInfo = get().discountInfo;

        const data: {
          price_model: string;
          redirect_url: string;
          discount_promo_code?: string;
        } = {
          price_model: priceModel,
          redirect_url: redirectUrl || window.location.href,
          discount_promo_code: undefined
        };
        const discountPlanModel = discountInfo?.plans?.find(
          (plan) => plan.price_model === priceModel
        );
        if (discountInfo && discountInfo.valid && discountPlanModel) {
          data.discount_promo_code = discountInfo.code.toUpperCase();
        }

        const response = await executeAPIRequest<SubscriptionResponse>({
          method: "post",
          url: "/io-user/payments/subscribe",
          options: {
            data
          }
        });

        return response;
      },
      getManageSubscriptionUrl: async () => {
        const response = await executeAPIRequest<ManageSubscriptionResponse>({
          method: "post",
          url: "/io-user/payments/manage-subscription",
          options: {
            data: {
              redirect_url: window.location.href
            }
          }
        });

        return response;
      },
      fetchSubscriptions: async () => {
        try {
          const response = await executeAPIRequest<SubscriptionStatusResponse>({
            method: "get",
            url: `/io-user/payments/subscription?${stringify({
              price_model: INTELLIGENCE_PRICE_MODEL // TODO: Ask BE to remove this parameter
            })}`
          });
          set({ currentSubscription: response.data });
          return response;
        } catch (error) {
          console.error(error);
          set({ currentSubscription: null });
          return null as SubscriptionStatusResponse | null;
        }
      },
      submitSupportRequest: async (message) => {
        // eslint-disable-next-line @typescript-eslint/no-explicit-any
        const response = await executeAPIRequest<any>({
          method: "post",
          url: "/io-user/support",
          options: {
            data: {
              description: message
            }
          }
        });

        return response;
      },
      discountInfo: null,
      validateDiscountCode: async (code: string) => {
        const normalizedCode = code.toUpperCase();

        try {
          const professionalPromise = executeAPIRequest<{
            data: {
              promo_code: string;
              amount_off: number;
              percent_off: number;
            };
            status: string;
          }>({
            method: "post",
            url: "/io-user/discounts/validate-promo-code",
            options: {
              data: {
                code: normalizedCode,
                product: INTELLIGENCE_PRICE_MODEL
              }
            }
          });
          const developerPromise = executeAPIRequest<{
            data: {
              promo_code: string;
              amount_off: number;
              percent_off: number;
            };
            status: string;
          }>({
            method: "post",
            url: "/io-user/discounts/validate-promo-code",
            options: {
              data: {
                code: normalizedCode,
                product: INTELLIGENCE_DEV_PRICE_MODEL
              }
            }
          });

          const [profResult, devResult] = await Promise.allSettled([
            professionalPromise,
            developerPromise
          ]);

          const successfulPlans: DiscountedPlan[] = [];

          if (profResult.status === "fulfilled" && profResult.value.status === "succeeded") {
            successfulPlans.push({
              price_model: INTELLIGENCE_PRICE_MODEL,
              amount_off: profResult.value.data.amount_off,
              percent_off: profResult.value.data.percent_off
            });
          }

          if (devResult.status === "fulfilled" && devResult.value.status === "succeeded") {
            successfulPlans.push({
              price_model: INTELLIGENCE_DEV_PRICE_MODEL,
              amount_off: devResult.value.data.amount_off,
              percent_off: devResult.value.data.percent_off
            });
          }

          if (successfulPlans.length > 0) {
            set({
              discountInfo: {
                code: normalizedCode,
                valid: true,
                plans: successfulPlans
              }
            });
            return true;
          } else {
            set({ discountInfo: { code: normalizedCode, valid: false, plans: [] } });
            return false;
          }
        } catch (e) {
          set({ discountInfo: { code: normalizedCode, valid: false, plans: [] } });
          return false;
        }
      },
      sendDripEvent: async (event: string) => {
        try {
          await executeAPIRequest<void>({
            method: "post",
            url: "/io-user/event",
            options: {
              data: { event_name: event }
            }
          });
        } catch (e) {
          console.error(e);
        }
      }
    }),
    {
      name: toStringWithLoginVersionSuffix("ionet_authentication"),
      storage: createJSONStorage(() => localCookies),
      partialize: (state) => {
        return {
          ...state,
          balance: undefined,
          balances: undefined,
          isLoadingBalances: undefined,
          burnRate: undefined,
          isLoggedIn: false,
          userId: undefined,
          isRequiresLoginModalOpen: false,
          requiresLoginMode: undefined,
          discountInfo: undefined
        } as unknown as UseUserStoreProps;
      },
      merge: (persistedState, currentState) => {
        const persisted = { ...(persistedState as Partial<UseUserStoreProps>) };
        delete persisted.userId;
        return {
          ...currentState,
          ...persisted
        };
      },
      onRehydrateStorage: () => {
        return (state, error) => {
          if (error) {
            console.log("an error happened during hydration", error);
          } else if (state) {
            queueMicrotask(() => {
              const persisted = state as Partial<UseUserStoreProps> & { theme?: string };
              const defaultAppTheme = getDefaultAppTheme();
              const appTheme = normalizeStoredTheme(
                persisted.appTheme ?? persisted.theme,
                defaultAppTheme
              );
              const explorerTheme = normalizeStoredTheme(
                persisted.explorerTheme,
                getDefaultExplorerTheme()
              );

              useUserStore.setState({ appTheme, explorerTheme });
              useUserStore.getState().applyThemeForCurrentMode();

              if (!env.LIGHT_MODE_ENABLED && appTheme === "light" && !isExplorerOnlyMode()) {
                useUserStore.getState().updateTheme("dark");
              }
            });
          }
        };
      }
    }
  )
);

const normaliseUserNotification = (result: UserNotificationResponse) => {
  const notification = getNotification(result.priority);

  return {
    id: result.id,
    text: result.message,
    createdAt: result.created_at,
    isRead: !!result.seen_at,
    title: result.topic.replace(/_/g, " ").toLowerCase(),
    objId: result.obj_id,
    objType: result.obj_type,
    ...notification
  } as UserNotification;
};

const normaliseUserPayout = (result: UserPayoutResponse) => {
  return {
    solscanUrl: result.solscan_url
  } as UserPayout;
};

export const normaliseUserTransaction = (result: UserTransactionResponse) => {
  return {
    ...result,
    txidUrl: result.txid_url,
    createdAt: result.created_at,
    sourceType: result.source_type,
    // sourceType: 'credits-job',
    // method: 'credits-expired',
    sourceId: result.source_id,
    originalAmount: result.original_amount,
    currencyConversionFeePrecent: result.currency_conversion_fee_precent,
    ionetFeePrecent: result.ionet_fee_precent,
    workerName: result.device_name,
    timeAmount: result.compute_minutes_served || 0 * 60,
    jobId: result.job_id,
    deviceId: result.device_id,
    paymentSource: result.payment_source,
    clusterName: result.cluster_name,
    clusterId: result.cluster_id,
    isPaygCharge: result.is_payg_charge
  } as UserTransaction;
};

const walletTypeAddress = (walletType: string, walletAddresses: UserWalletType[]) => {
  return walletAddresses.filter((wallet) => wallet.wallet_type === walletType);
};
